import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { gethistoryData, downloadProcessedData, downloadInputFile } from "../model/IfsProcessingModel";

const IfsHistoryVM = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    fetchHistoryData();
  }, []);

  const fetchHistoryData = async () => {
    try {
      setIsLoading(true);
      const response = await gethistoryData("IFS");
      
      if (response && response.data) {
        setRecords(response.data);
      }
    } catch (error) {
      console.error("Error fetching history data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleGoHome = () => {
    navigate("/ifs-operation-centre");
  };

  const handleCloseBackdrop = () => {
    // Backdrop cannot be closed during download
  };

  const handleDownloadOutput = async (batchId, modelName) => {
    try {
      setIsDownloading(true);
      const response = await downloadProcessedData("IFS Operations", batchId, modelName);
      
      if (response && response.base64Data) {
        const byteCharacters = atob(response.base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `IFS_Operations_Output_${batchId}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error downloading output file:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadInput = async (batchId) => {
    try {
      setIsDownloading(true);
      const response = await downloadInputFile("IFS Operations", batchId);
      
      if (response && response.base64Data) {
        const byteCharacters = atob(response.base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `IFS_Operations_Input_${batchId}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error downloading input file:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleViewDetails = (batchId, modelName, totalRecords) => {
    navigate(`/ifs-operation-centre?batchId=${batchId}&modelName=${modelName}&totalRecords=${totalRecords}&toBeProcessed=true`);
  };

  const getStatusCellConfig = (record) => {
    const status = record.status?.toLowerCase();
    
    const standardButtonStyles = {
      borderRadius: '12px',
      fontSize: '0.625rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      padding: '2px 6px !important',
      minWidth: '120px !important',
      width: '120px !important',
      height: '24px !important',
      maxWidth: '120px',
      lineHeight: '1',
      minHeight: '24px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    };
    
    if (status === "completed") {
      return {
        type: "button",
        config: {
          label: "Completed",
          onClick: undefined,
          disabled: true,
          sx: {
            ...standardButtonStyles,
            backgroundColor: '#4caf50',
            color: '#ffffff',
            border: '1px solid #388e3c',
            cursor: 'default',
            pointerEvents: 'none',
            '&:hover': {
              backgroundColor: '#4caf50',
            },
            '&.Mui-disabled': {
              backgroundColor: '#4caf50',
              color: '#ffffff',
              opacity: 1,
            }
          }
        }
      };
    } else if (status === "processing" || status === "in progress") {
      return {
        type: "button",
        config: {
          label: status === "processing" ? "Processing" : "In Progress",
          onClick: undefined,
          disabled: true,
          sx: {
            ...standardButtonStyles,
            backgroundColor: '#ff9800',
            color: '#ffffff',
            border: '1px solid #f57c00',
            cursor: 'default',
            pointerEvents: 'none',
            '&.Mui-disabled': {
              backgroundColor: '#ff9800',
              color: '#ffffff',
              opacity: 1,
            }
          }
        }
      };
    } else if (status === "pending") {
      return {
        type: "button",
        config: {
          label: "Process",
          onClick: () => handleViewDetails(record.batch_id, record.model_name, record.total_records),
          disabled: false,
          sx: {
            ...standardButtonStyles,
            backgroundColor: '#2196f3',
            color: '#ffffff',
            border: '1px solid #1976d2',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#1976d2',
            }
          }
        }
      };
    } else if (status === "failed") {
      return {
        type: "button",
        config: {
          label: "Failed",
          onClick: undefined,
          disabled: true,
          sx: {
            ...standardButtonStyles,
            backgroundColor: '#f44336',
            color: '#ffffff',
            border: '1px solid #d32f2f',
            cursor: 'default',
            pointerEvents: 'none',
            '&.Mui-disabled': {
              backgroundColor: '#f44336',
              color: '#ffffff',
              opacity: 1,
            }
          }
        }
      };
    }
    
    return {
      type: "box",
      config: {
        content: status || "Unknown",
        sx: {
          ...standardButtonStyles,
          cursor: 'default',
          backgroundColor: '#9e9e9e',
          color: '#ffffff',
          border: '1px solid #757575'
        }
      }
    };
  };

const columns = [
  {
    field: "file_name",
    header: "File Name",
    minWidth: "150px",
    render: (record) => ({
      type: "text",
      content: (record.tool_name && record.model_name) 
        ? `${record.tool_name.replace(/[\s-]+/g, '_')}_${record.model_name.replace(/[\s-]+/g, '_')}` 
        : "IFS_Operations"
    })
  },
  {
    field: "date_time",
    header: "Date",
    align: "center",
    minWidth: "80px",
    render: (record) => ({
      type: "text",
      content: record.created_at ? new Date(record.created_at).toLocaleDateString('en-CA') : "-"
    })
  },
  {
    field: "unique_records",
    header: "Unique",
    align: "center",
    minWidth: "110px",
    render: (record) => ({
      type: "text",
      content: record.unique_records?.toLocaleString() || "0"
    })
  },
  {
    field: "processed_records",
    header: "Processed",
    align: "center",
    minWidth: "110px",
    render: (record) => ({
      type: "text",
      content: record.processed_records?.toLocaleString() || "0"
    })
  },
  {
    field: "total_records",
    header: "Total",
    align: "center",
    minWidth: "100px",
    render: (record) => ({
      type: "text",
      content: record.total_records?.toLocaleString() || "0"
    })
  },
  {
    field: "accuracy_percentage",
    header: "Accuracy",
    align: "center",
    minWidth: "90px",
    render: (record) => {
      const accuracy = record.accuracy_percentage || 0;
      const getAccuracyColor = (percentage) => {
        if (percentage === null || percentage === undefined) return "default";
        if (percentage >= 76) return "success";
        if (percentage >= 51) return "warning";
        return "error";
      };
      
      const color = getAccuracyColor(accuracy);
      
      return {
        type: "box",
        config: {
          content: `${accuracy}%`,
          sx: {
            height: '24px',
            minHeight: '24px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '12px',
            fontSize: '0.625rem',
            fontWeight: 600,
            padding: '2px 6px',
            minWidth: '60px',
            border: `1px solid ${color === 'success' ? '#4caf50' : color === 'warning' ? '#ff9800' : '#f44336'}`,
            backgroundColor: `${color === 'success' ? 'rgba(76, 175, 80, 0.1)' : color === 'warning' ? 'rgba(255, 152, 0, 0.1)' : 'rgba(244, 67, 54, 0.1)'}`,
            color: `${color === 'success' ? '#2e7d32' : color === 'warning' ? '#e65100' : '#d32f2f'}`
          }
        }
      };
    }
  },
  {
    field: "status",
    header: "Status",
    align: "center",
    minWidth: "130px",
    render: (record) => getStatusCellConfig(record)
  },
  {
    field: "actions",
    header: "Actions",
    align: "center",
    minWidth: "70px",
    render: (record) => {
      const statusLower = record.status?.toLowerCase();
      const isCompleted = statusLower === "completed";
      const isProcessed = statusLower === "processed";
      const canDownload = isCompleted || isProcessed;

      return {
        type: "iconButton",
        config: {
          icon: <FileDownloadIcon style={{ color: canDownload ? "#002A5B" : "#cccccc" }} />,
          onClick: canDownload ? () => handleDownloadOutput(record.batch_id, record.model_name) : () => {},
          disabled: !canDownload,
          tooltip: canDownload ? "Download Output" : `Cannot download (Status: ${record.status})`,
          sx: {
            cursor: canDownload ? 'pointer' : 'not-allowed',
            opacity: canDownload ? 1 : 0.5,
            '&:disabled': {
              cursor: 'not-allowed',
              opacity: 0.5
            }
          }
        }
      };
    }
  }
];

  const filteredRecords = useMemo(() => {
    if (!searchTerm) return records;
    
    return records.filter(record => 
      Object.values(record).some(value => 
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [records, searchTerm]);

  return {
    searchTerm,
    page,
    rowsPerPage,
    filteredRecords,
    isLoading,
    isDownloading,
    columns,
    handleSearchChange,
    handleChangePage,
    handleChangeRowsPerPage,
    handleGoHome,
    handleCloseBackdrop,
    getStatusCellConfig
  };
};

export default IfsHistoryVM;